import { gql, useQuery } from '@provision/graphql-client';
import { useCallback, useEffect, useMemo } from 'react';
import { isNullish } from 'remeda';
import { MAX_QUERY_LIMIT } from 'src/graphql/constants';
import { Records } from 'src/graphql/types';
import { useGroupRulesStore } from 'src/store/group-rules';
import { PartnerGroupRules, RuleValues } from './group-rules.types';

const GET_GROUP_RULE_VALUES_QUERY = gql`
  query GetGroupRuleValues @entity(name: "PartnerGroupRule") {
    getPartnerGroupRule {
      records {
        id
        name
      }
    }
  }
`;

interface GetGroupRuleValues {
  getPartnerGroupRule: Records<{
    id: string;
    name: string;
  }>;
}

/** fetched once at app boot; refetch after partner list changes (e.g. create partner). */
export const GET_PARTNER_GROUP_RULE_VALUES_QUERY = gql`
  query GetPartnerGroupRuleValues($params: QueryParams)
  @entity(name: "PartnerGroupRuleValue") {
    getPartner(queryParams: $params) {
      records {
        id
        partnerGroups {
          id
          name
          partnerGroupId
          ruleValues {
            id
            value
            partnerGroupRuleId
          }
        }
      }
    }
  }
`;

interface GetPartnerRulesQuery {
  getPartner: Records<{
    id: number;
    partnerGroups: {
      ruleValues: {
        id: string;
        value: string;
        partnerGroupRuleId: PartnerGroupRules;
      }[];
    }[];
  }>;
}

export const usePartnerGroupRules = (partnerId?: number | null) => {
  const selectRules = useCallback(
    (s: ReturnType<typeof useGroupRulesStore.getState>) =>
      isNullish(partnerId)
        ? s.allRulesDefault
        : (s.rulesByPartner.get(partnerId) ?? s.allRulesDefault),
    [partnerId],
  );

  const partnerGroupRules = useGroupRulesStore(selectRules);
  const loading = useGroupRulesStore((s) => s.partnerRulesLoading);

  return { partnerGroupRules, loading };
};

export const useFetchPartnerGroupRules = () => {
  const { data: groupRuleValuesData } = useQuery<GetGroupRuleValues>(
    GET_GROUP_RULE_VALUES_QUERY,
  );

  const allRuleIds = useMemo(
    () => (groupRuleValuesData?.getPartnerGroupRule?.records ?? []).map((r) => r.id),
    [groupRuleValuesData?.getPartnerGroupRule],
  ) as PartnerGroupRules[];

  const setAllRulesDefault = useGroupRulesStore((s) => s.setAllRulesDefault);
  useEffect(() => {
    if (allRuleIds.length) setAllRulesDefault(allRuleIds);
  }, [allRuleIds, setAllRulesDefault]);

  const { data, loading, ...rest } = useQuery<GetPartnerRulesQuery>(
    GET_PARTNER_GROUP_RULE_VALUES_QUERY,
    {
      context: { omitIsolationModeHeader: true },
      variables: { params: { limit: MAX_QUERY_LIMIT } },
    },
  );

  const setRulesByPartner = useGroupRulesStore((s) => s.setRulesByPartner);

  useEffect(() => {
    const partners = data?.getPartner?.records ?? [];
    if (!partners.length || !allRuleIds.length) return;

    const template = allRuleIds.reduce(
      (acc, id) => {
        acc[id] = false;
        return acc;
      },
      {} as Record<PartnerGroupRules, boolean>,
    );

    // Partner group values use boolean strings; the full-list user-group token ("*") is not used here.
    const toBool = (v: string) => v.toLowerCase() === RuleValues.True;

    const byPartner = new Map<number, Record<PartnerGroupRules, boolean>>();

    partners.forEach((p) => {
      const obj = { ...template };
      const seen = new Set<PartnerGroupRules>();

      p.partnerGroups.forEach((g) => {
        g.ruleValues.forEach((rv) => {
          if (seen.has(rv.partnerGroupRuleId)) return;
          obj[rv.partnerGroupRuleId] = toBool(rv.value);
          seen.add(rv.partnerGroupRuleId);
        });
      });

      byPartner.set(p.id, obj);
    });

    setRulesByPartner(byPartner);
  }, [data, allRuleIds, setRulesByPartner]);

  return { ...rest, loading };
};
